// ─────────────────────────────────────────────────────────────────────────────
// Aidey Feature Guides
// Each key matches a tourId used by useAutoTour() and HelpButton.
// Steps: title (string) + body (string, plain text, British English, no em dashes).
// ─────────────────────────────────────────────────────────────────────────────

export const allGuides = {

  // ── DASHBOARD ──────────────────────────────────────────────────────────────
  dashboard: {
    steps: [
      {
        title: 'Your Dashboard',
        body: 'This is your starting point each day. It pulls together your tasks, invoices, calendar, and client activity so you can see where things stand at a glance.',
      },
      {
        title: 'Summary cards',
        body: 'The row of cards at the top shows your key numbers: active tasks, unpaid invoices, and upcoming meetings. Tap any card to jump straight to that section.',
      },
      {
        title: 'Morning Briefing',
        body: 'Aidey writes a short briefing each morning based on your calendar, tasks, and invoices. Read it to start your day with context rather than guesswork.',
      },
      {
        title: 'Your week',
        body: 'The week view shows everything scheduled and due. Tap any day to see its tasks and meetings in full.',
      },
      {
        title: 'Aidey Chat',
        body: 'Tap the chat bubble in the corner to talk to Aidey. You can ask it to create a task, draft an email, look up an invoice, or anything else related to your work.',
      },
    ],
  },

  // ── CRM ────────────────────────────────────────────────────────────────────
  crm: {
    steps: [
      {
        title: 'Your CRM Pipeline',
        body: 'This board tracks every client and prospect through your sales process. Each column is a stage, and each card is a contact.',
      },
      {
        title: 'Moving contacts',
        body: 'Drag a contact card left or right to move it to the next stage. The stage updates immediately and is saved to the contact record.',
      },
      {
        title: 'Adding a contact',
        body: 'Tap "Add Contact" at the top right to create a new contact. They appear in the first column of your pipeline automatically.',
      },
      {
        title: 'Opening a contact',
        body: 'Tap any card to open the full contact record. Inside you will find interaction history, linked invoices, health score, and notes.',
      },
      {
        title: 'Health Score',
        body: 'The coloured badge on each card is the client health score. It reflects recent contact frequency, open work, and invoice status. Red means attention needed.',
      },
    ],
  },

  // ── PROJECTS ───────────────────────────────────────────────────────────────
  projects: {
    steps: [
      {
        title: 'Your Projects',
        body: 'All active projects live here, both client work and internal projects. The summary row at the top shows total active projects and any overdue tasks across all of them.',
      },
      {
        title: 'Opening a project',
        body: 'Tap any project card or row to open it. Inside you will find tabs for tasks, files, time tracking, finances, and notes.',
      },
      {
        title: 'Creating a project',
        body: 'Tap "New Project" to create one. You can start from a blank project or pick a workflow template that sets up the task structure for you.',
      },
      {
        title: 'Switching views',
        body: 'Use the view toggle near the top to switch between list view and card view. Sort by deadline, client, or status depending on what you need to see.',
      },
    ],
  },

  // ── INVOICING ──────────────────────────────────────────────────────────────
  invoicing: {
    steps: [
      {
        title: 'Invoicing and Finance',
        body: 'This section covers invoices, quotes, and expenses. Use the left-hand menu to switch between them.',
      },
      {
        title: 'Creating an invoice',
        body: 'Go to Invoices and tap "New Invoice". Fill in the client, line items, and due date, then tap Send to email it directly from Aidey.',
      },
      {
        title: 'Quotes',
        body: 'Go to Quotes to send a price breakdown before any work starts. Once a client accepts a quote, you can convert it to an invoice in one tap.',
      },
      {
        title: 'Expenses',
        body: 'Log business costs under Expenses. Categorise them, attach receipts, and mark them as billable so they can be added to a client invoice later.',
      },
      {
        title: 'Tracking payment',
        body: 'Each invoice shows its status: Draft, Sent, Viewed, or Paid. Tap an invoice to mark it paid or to send a reminder.',
      },
    ],
  },

  // ── SCHEDULING HUB ─────────────────────────────────────────────────────────
  scheduling: {
    steps: [
      {
        title: 'Your Scheduling Hub',
        body: 'This is where you set your availability, create meeting types, view bookings, and manage meeting polls. Use the tabs at the top to navigate.',
      },
      {
        title: 'Setting availability',
        body: 'Go to the Availability tab and set your working hours for each day. Add buffer time between meetings and block out any days you do not want bookings.',
      },
      {
        title: 'Event types',
        body: 'Go to Event Types to create different meeting formats, such as a 30-minute intro call or a 90-minute workshop. Each gets its own booking link to share with clients.',
      },
      {
        title: 'Bookings',
        body: 'The Bookings tab shows every confirmed meeting. You can confirm, cancel, or delete bookings from here.',
      },
      {
        title: 'Meeting polls',
        body: 'Polls let you propose multiple time slots to a group and let everyone vote on the best one. Tap the Polls tab, then "+ New Poll" to create one.',
      },
    ],
  },

  // ── SCHEDULER / BOOKING PAGES ──────────────────────────────────────────────
  scheduler: {
    steps: [
      {
        title: 'Client Booking Pages',
        body: 'The Scheduler lets clients book time with you without back-and-forth emails. You share a link and they pick a slot that fits your availability.',
      },
      {
        title: 'Creating a meeting type',
        body: 'Tap "New Meeting Type" to set up a bookable meeting. Give it a name, set the duration, and add any questions you want clients to answer when they book.',
      },
      {
        title: 'Your booking link',
        body: 'Each meeting type has its own booking link. Copy it from the meeting type card and paste it into your email signature, website, or anywhere clients can see it.',
      },
      {
        title: 'Toggling availability',
        body: 'You can pause any meeting type to stop new bookings without deleting it. Tap the toggle on the card to enable or disable it.',
      },
    ],
  },

  // ── HR / TEAM ──────────────────────────────────────────────────────────────
  hr: {
    steps: [
      {
        title: 'HR and Team Management',
        body: 'This is your HR hub. It shows every team member, their current status, and any alerts that need your attention.',
      },
      {
        title: 'Team alerts',
        body: 'The Alerts column flags things that need action, such as probation periods ending soon, expiring documents, or outstanding leave requests. Tap an alert to resolve it.',
      },
      {
        title: 'Opening a profile',
        body: 'Tap any team member row to open their full HR profile. Inside you will find performance notes, leave history, uploaded documents, and onboarding progress.',
      },
      {
        title: 'Leave requests',
        body: 'Leave requests from your team appear here for approval. Tap a request to see the dates, reason, and remaining entitlement, then approve or decline.',
      },
    ],
  },

  // ── EXPENSES ───────────────────────────────────────────────────────────────
  expenses: {
    steps: [
      {
        title: 'Expenses',
        body: 'Log and track every business expense here. Everything you add feeds into your financial overview and can be linked to a client invoice.',
      },
      {
        title: 'Adding an expense',
        body: 'Tap "New Expense" to log a cost. Enter the amount, category, supplier, and date. Tick "Billable" if you plan to charge it back to a client.',
      },
      {
        title: 'Filtering expenses',
        body: 'Use the filter bar to narrow expenses by date range, category, or client. This is useful when preparing for tax returns or checking spend by area.',
      },
      {
        title: 'Adding to an invoice',
        body: 'Any expense marked billable can be pulled into a client invoice. Open the invoice, tap "Add expense", and select the items to include.',
      },
    ],
  },

  // ── SETTINGS ───────────────────────────────────────────────────────────────
  settings: {
    steps: [
      {
        title: 'Settings',
        body: 'Settings is where you configure Aidey to work the way you do. Start with your profile, then connect your email and calendar.',
      },
      {
        title: 'Profile',
        body: 'Go to Profile and fill in your name, company details, and communication preferences. Aidey uses these to personalise its suggestions and drafts.',
      },
      {
        title: 'Email connections',
        body: 'Go to Email Connections to link your Gmail or Outlook account. Once connected, Aidey can read your inbox, draft replies, and action emails on your behalf.',
      },
      {
        title: 'Calendar and video',
        body: 'Connect your calendar under Calendar Connections so Aidey knows your availability. You can also link Zoom or Google Meet here.',
      },
      {
        title: 'Plan and billing',
        body: 'See your current plan, usage limits, and billing details under Plan and Billing. Upgrade or change your plan from here at any time.',
      },
    ],
  },

  // ── AIDEY CHAT ─────────────────────────────────────────────────────────────
  'aidey-chat': {
    steps: [
      {
        title: 'Aidey Chat',
        body: 'Aidey Chat is your AI assistant. Ask it anything about your work and it will answer, create, or take action on your behalf.',
      },
      {
        title: 'Starting a conversation',
        body: 'Type your message in the box at the bottom and tap Send. Try "What tasks are overdue?" or "Draft a follow-up email to Sarah at Acme."',
      },
      {
        title: 'What Aidey can do',
        body: 'Aidey can create tasks, schedule meetings, pull invoice totals, draft emails, summarise your week, and look things up across your account. Give it as much context as you can.',
      },
      {
        title: 'Chat history',
        body: 'Previous conversations are saved in the left panel. Tap any conversation to continue it, or tap "New Chat" to start fresh.',
      },
    ],
  },

  // ── WORKFLOW EDITOR ────────────────────────────────────────────────────────
  workflows: {
    steps: [
      {
        title: 'Workflow Editor',
        body: 'The Workflow Editor lets you customise the stages used in your CRM pipeline and project boards. Changes take effect immediately across all boards using that workflow.',
      },
      {
        title: 'Choosing a template',
        body: 'Pick a system template as a starting point, or create a workflow from scratch. Templates cover common setups like client onboarding, project delivery, and sales pipelines.',
      },
      {
        title: 'Editing stages',
        body: 'Tap any stage name to rename it. Drag stages to reorder them. Tap the bin icon to delete a stage. Add a new stage with the "+ Add Stage" button.',
      },
      {
        title: 'Saving changes',
        body: 'Tap Save when you are done. All CRM boards and project boards linked to this workflow will update straight away to reflect the new stages.',
      },
    ],
  },

  // ── FAMILY ─────────────────────────────────────────────────────────────────
  family: {
    steps: [
      { title: 'Your household',
        body: 'This is where everyone in your home lives inside Aidey. Add a person for each member of the family, and Aidey can keep track of what belongs to who.' },
      { title: 'Adding someone',
        body: 'Tap Add member to add a person. You can note their birthday, any allergies, and who their doctor or dentist is, so it is all in one place when you need it.' },
      { title: 'Logins for children',
        body: 'Children can have their own login with a simple username. They see a smaller, safer version of Aidey with their own jobs and stars.' },
      { title: 'Stars and rewards',
        body: 'Set up rewards that children can earn by doing their jobs. They collect stars, and when they have enough they can swap them for a reward you have chosen.' },
      { title: 'Keeping it separate',
        body: 'Your family things stay in your family space. Work never mixes in, and work never sees any of this.' },
    ],
  },

  // ── SHOPPING ───────────────────────────────────────────────────────────────
  shopping: {
    steps: [
      { title: 'Your shopping lists',
        body: 'Make a list for anything you like. A weekly food shop, a birthday party, bits for the house. You can have as many lists as you want.' },
      { title: 'Adding things',
        body: 'Type what you need and add it to the list. You can put a number next to it too, like six eggs or two loaves.' },
      { title: 'Ticking things off',
        body: 'Tap an item when it is in the trolley and it gets crossed off. Tap it again if you change your mind.' },
      { title: 'Your own lists',
        body: 'Your lists are yours. Everyone in the house keeps their own, so nothing gets muddled up or ticked off by mistake.' },
      { title: 'Ask Aidey',
        body: 'You can just tell Aidey what you need instead of typing it in here. Say add bread and bin bags to the shopping list and it will do it for you.' },
    ],
  },

  // ── BUDGET ─────────────────────────────────────────────────────────────────
  budget: {
    steps: [
      { title: 'Your money in one place',
        body: 'This is where you keep an eye on what comes in and what goes out, so nothing takes you by surprise.' },
      { title: 'What you spend',
        body: 'Add the things you pay for regularly. Aidey works out what is left over once they are all paid.' },
      { title: 'Saving up',
        body: 'Set up a savings goal for something you are working towards. Add money to it whenever you can and watch it grow.' },
      { title: 'Safe to spend',
        body: 'Aidey looks at what is still due before your next payday and tells you what you can safely spend without running short.' },
      { title: 'Private to you',
        body: 'Your money is in your personal space. Nobody at work can see any of it.' },
    ],
  },

  // ── MEAL PLANNER ───────────────────────────────────────────────────────────
  mealplanner: {
    steps: [
      { title: 'What is for dinner',
        body: 'Plan your meals for the week so you are not stood staring in the fridge at six o clock.' },
      { title: 'Filling in the week',
        body: 'Tap any day to add a meal. Tap it again to change it, or clear it if plans change.' },
      { title: 'Stuck for ideas',
        body: 'Tell Aidey what is in your cupboards and it will suggest something you can actually make. It knows about any allergies in your household.' },
      { title: 'Straight to the list',
        body: 'Once you know what you are cooking, ask Aidey to put what you need on the shopping list.' },
    ],
  },

  // ── RENEWALS ───────────────────────────────────────────────────────────────
  renewals: {
    steps: [
      { title: 'Never get caught out',
        body: 'Keep track of anything that renews. Insurance, your MOT, subscriptions, your passport. Aidey will tell you before it happens.' },
      { title: 'Adding one',
        body: 'Tap Add renewal and fill in what it is, when it is due, and how much notice you want. Aidey handles the rest.' },
      { title: 'Health check ups too',
        body: 'Use the Health category for dentist and optician appointments. You can set these to repeat every two, three or five years, which suits the ones that come round slowly.' },
      { title: 'What it all costs',
        body: 'The card at the top adds up what your renewals cost you over a year, so you can see it all in one number.' },
      { title: 'Who it is for',
        body: 'You can say who a renewal belongs to. Handy when two people in the house both have a car or a phone contract.' },
    ],
  },

  // ── BIRTHDAYS ──────────────────────────────────────────────────────────────
  birthdays: {
    steps: [
      { title: 'Birthdays and anniversaries',
        body: 'Put in the dates you do not want to forget and Aidey will remind you in good time.' },
      { title: 'Adding a date',
        body: 'Add the person and the date. If you know the year they were born, Aidey can work out how old they are turning.' },
      { title: 'Time to buy a card',
        body: 'You get a nudge before the day, not on the morning of it, so there is still time to sort a present.' },
      { title: 'Everyone in the family',
        body: 'Add dates for anyone, not just people in your household. Grandparents, friends, whoever matters.' },
    ],
  },

  // ── JOURNAL ────────────────────────────────────────────────────────────────
  journal: {
    steps: [
      { title: 'Your journal',
        body: 'A private place to write things down. How your day went, something you want to remember, anything at all.' },
      { title: 'Writing an entry',
        body: 'Start a new entry and write as much or as little as you like. You can add a photo too.' },
      { title: 'Keeping it tidy',
        body: 'Sort your entries into categories so you can find things again later.' },
      { title: 'Only you',
        body: 'Your journal is private. Nobody else can read it, not even other people in your household.' },
    ],
  },

  // ── FAMILY CHAT ────────────────────────────────────────────────────────────
  familychat: {
    steps: [
      { title: 'Family chat',
        body: 'A place for everyone at home to talk. Ask what time tea is, say you are running late, share the good news first.' },
      { title: 'Everyone is here',
        body: 'Everyone in your household can see and join in, including children with their own logins.' },
      { title: 'Separate from work',
        body: 'This is only for your family. Nothing here ever shows up on the work side.' },
    ],
  },

};
